import React, { useEffect, useRef, useState } from 'react';
import type { SpiritedSegment } from '../types';
import { getCartoTileUrl } from '../utils/mapUtils';
import { getSectorPathSlice } from '../utils/raceSector';

declare const L: any;

interface RouteThumbnailMapProps {
    path: [number, number][];
    spiritedSegment?: SpiritedSegment;
    start?: [number, number];
    finish?: [number, number];
}

/**
 * Static, non-interactive route preview used in event card thumbnails.
 * The Leaflet map is only created once the card scrolls into view.
 */
const RouteThumbnailMap: React.FC<RouteThumbnailMapProps> = ({ path, spiritedSegment, start, finish }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const [isVisible, setIsVisible] = useState(false);

    // Lazy-mount when the thumbnail enters the viewport
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) {
                setIsVisible(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible || !containerRef.current || path.length < 2) return;

        const map = L.map(containerRef.current, {
            zoomControl: false,
            attributionControl: false,
            dragging: false,
            touchZoom: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            keyboard: false,
            zoomSnap: 0.25,
        });
        mapRef.current = map;

        L.tileLayer(getCartoTileUrl('dark_all'), { maxZoom: 19 }).addTo(map);

        const route = L.polyline(path, {
            color: '#4f46e5', // indigo-600
            weight: 4,
            opacity: 0.9,
            lineCap: 'round',
            lineJoin: 'round',
            interactive: false,
        }).addTo(map);

        const sectorPath = getSectorPathSlice(path, spiritedSegment);
        if (sectorPath.length > 1) {
            L.polyline(sectorPath, {
                color: '#06b6d4', // cyan-500
                weight: 5,
                opacity: 0.95,
                lineCap: 'round',
                lineJoin: 'round',
                interactive: false,
            }).addTo(map);
        }

        const dot = (latLng: [number, number], color: string) => L.circleMarker(latLng, {
            radius: 4,
            color: '#ffffff',
            weight: 1.5,
            fillColor: color,
            fillOpacity: 1,
            interactive: false,
        }).addTo(map);

        const parseCoord = (coord: any): [number, number] | null => {
            if (!coord) return null;
            if (Array.isArray(coord) && coord.length >= 2) {
                const lat = Number(coord[0]);
                const lon = Number(coord[1]);
                if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) return [lat, lon];
            } else if (typeof coord === 'object') {
                const lat = Number(coord.lat ?? coord.latitude);
                const lon = Number(coord.lon ?? coord.lng ?? coord.longitude);
                if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) return [lat, lon];
            }
            return null;
        };

        const startPt = parseCoord(start) || path[0];
        const finishPt = parseCoord(finish) || path[path.length - 1];
        const isLoop = Math.abs(startPt[0] - finishPt[0]) < 0.0005 && Math.abs(startPt[1] - finishPt[1]) < 0.0005;

        if (!isLoop) {
            dot(finishPt, '#ef4444');
        }
        dot(startPt, isLoop ? '#a855f7' : '#10b981');

        const fit = () => map.fitBounds(route.getBounds(), { padding: [18, 18] });
        fit();
        // Re-fit once the card layout has settled
        const timer = setTimeout(() => {
            if (mapRef.current) {
                mapRef.current.invalidateSize();
                fit();
            }
        }, 150);

        return () => {
            clearTimeout(timer);
            map.remove();
            mapRef.current = null;
        };
    }, [isVisible, path, spiritedSegment, start, finish]);

    return <div ref={containerRef} className="w-full h-full bg-gray-900 pointer-events-none isolate" />;
};

export default RouteThumbnailMap;
